import type { Component } from "solid-js";
import { createEffect, createMemo, createSignal, getOwner, Match, runWithOwner, Show, Switch } from "solid-js";
import type { RouteSectionProps } from "@solidjs/router";
import { useNavigate } from "@solidjs/router";
import ApiService from "../../services/ApiService";
import FormId from "../../state/FormId";
import { useQuery } from "@tanstack/solid-query";
import { toForm } from "../../utils/mappers/form";
import { minutes } from "../../utils/timeUtil";
import { useCustomerState } from "../../state/customer";
import logger from "../../utils/logger";
import { Breadcrumb } from "../../components/breadcrumb/Breadcrumb";
import { useTranslation } from "../../state/i18n";
import IconSelect from "../../components/inputs/IconSelect";
import { MAX_CHARACTERS_MD, MAX_CHARACTERS_SM, SELECTABLE_FORM_ICONS } from "../../constants";
import { toLegacyPlatformIcon } from "../../utils/iconUtil";
import type { MoreAppError } from "../../MoreAppError";
import PermissionService from "../../services/PermissionService";
import type { Form } from "../../state/form";
import { formSchema } from "../../state/form";
import GenericFailureModal from "../GenericFailureModal";
import InlineEditor from "../../components/inlineEditor/InlineEditor";
import { pickSchema } from "../../utils/validationUtil";
import Text from "../../components/text/Text";
import { IconButton } from "../../components/button/Button";
import GlobalTabs from "../../components/tabs/GlobalTabs";
import FolderId from "../../state/FolderId";
import ResourcePermissionsContent from "./ResourcePermissionsContent";
import AddPermissionModal from "./AddPermissionModal";
import EditFormButton from "./form/EditFormButton";
import DeleteFormButton from "./form/DeleteFormButton";
import DeleteFormModal from "./folder/DeleteFormModal";
import DirectUrlButton from "./form/DirectUrlButton";
import { IconButtonMenu } from "../../components/listItemMenu/ListItemMenu";
import TrustService from "../../services/TrustService";
import { isNil } from "lodash-es";
import SubmissionTable from "./form/SubmissionsTable";
import { ResourceNotFoundContent } from "../ResourceNotFoundPage";
import { doNotRetryOnUnrecoverableError, NON_RETRYABLE_STATUS_CODES } from "../../utils/apiUtil";
import TasksContent from "./form/TasksContent";
import { useModalManager } from "../../components/modal/ModalManager";
import { keyFromSelector } from "i18next";

const FormPage: Component<RouteSectionProps> = (props) => {
  const { t } = useTranslation();
  const customer = useCustomerState();
  const [tab, setTab] = createSignal<string>();

  const formId = createMemo(() => FormId.from(props.params.formId!));
  const folderId = createMemo(() => FolderId.from(props.params.folderId!));
  const formQuery = createMemo(() =>
    useQuery(() => ({
      queryKey: ["forms", formId().toString()],
      queryFn: async () => {
        const data = await ApiService.getForm(formId());
        return toForm(data, undefined);
      },
      staleTime: minutes(10),
      retry: doNotRetryOnUnrecoverableError,
    })),
  );

  return (
    <>
      <Show when={!isNil(formQuery().error) && NON_RETRYABLE_STATUS_CODES.includes(formQuery().error!.status)}>
        <ResourceNotFoundContent
          resource={keyFromSelector(($) => $.RESOURCE_FORM)}
          illustration={{ primaryIcon: "document" }}
        />
      </Show>
      <Show when={formQuery().data}>
        {(form) => (
          <div class="flex h-full flex-col">
            <Breadcrumb
              crumbs={[
                {
                  label: t(($) => $.FORM_PAGE_BACK_TO_FOLDER),
                  href: `/customers/${customer.customerId}/folders/${folderId()}`,
                },
              ]}
            />

            <Header folderId={folderId()} form={form()} onPermissionAdded={() => setTab("permissions")} />

            <div class="mt-4 min-h-0 flex-1">
              <GlobalTabs
                value={tab}
                onTabChange={(tab) => setTab(tab)}
                items={[
                  {
                    key: "submissions",
                    visible: PermissionService.canReadFormRegistrations(customer.customerId, formId()),
                    label: () => t(($) => $.FORM_PAGE_SUBMISSIONS_TAB_TITLE),
                    Slot: () => (
                      <div class="flex h-full flex-col py-6">
                        <SubmissionTable
                          customerId={customer.customerId}
                          formId={form().id}
                          formVersionId={form().publishedVersion?.formVersionId}
                          formName={form().meta.name}
                        />
                      </div>
                    ),
                  },
                  {
                    key: "tasks",
                    visible: PermissionService.canReadFormTasks(customer.customerId, formId()),
                    label: () => t(($) => $.FORM_PAGE_TASKS_TAB_TITLE),
                    Slot: () => (
                      <div class="flex h-full flex-col py-6">
                        <TasksContent customerId={customer.customerId} form={form()} />
                      </div>
                    ),
                  },
                  {
                    key: "permissions",
                    visible: PermissionService.canManagePermissions(customer.customerId),
                    label: () => t(($) => $.FORM_PAGE_PERMISSIONS_TAB_TITLE),
                    Slot: () => (
                      <div>
                        <ResourcePermissionsContent resourceType="FORM" resourceId={form().id} />
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          </div>
        )}
      </Show>
    </>
  );
};

const Header: Component<{ folderId: FolderId; form: Form; onPermissionAdded: () => void }> = (props) => {
  const owner = getOwner();
  const { t } = useTranslation();
  const { open } = useModalManager();
  const customer = useCustomerState();
  const canEditForm = createMemo(() => PermissionService.canEditForm(customer.customerId, props.form.id));
  const hasPublishedFormVersion = createMemo(() => !isNil(props.form.publishedVersion));
  const canShareForm = createMemo(
    () =>
      hasPublishedFormVersion() &&
      TrustService.isTrusted(customer.customerId) &&
      PermissionService.canShareForm(customer.customerId, props.form.id),
  );
  const canManagePermissions = createMemo(() => PermissionService.canManagePermissions(customer.customerId));
  const canDeleteForm = createMemo(() => PermissionService.canDeleteForm(customer.customerId, props.form.id));

  const collapseActionsIntoMenu = createMemo(() => canShareForm() && canManagePermissions() && canDeleteForm());
  const [isEditingFormName, setIsEditingFormName] = createSignal(false);
  const [isEditingFormDescription, setIsEditingFormDescription] = createSignal(false);

  const openErrorModal = (err: MoreAppError) => open({ content: () => <GenericFailureModal error={err} /> });

  const openAddPermission = () =>
    open({
      content: () => (
        <AddPermissionModal resourceType="FORM" resourceId={props.form.id} onSuccess={props.onPermissionAdded} />
      ),
    });
  const openDeleteForm = () =>
    open({
      content: () => (
        <DeleteFormModal
          customerId={customer.customerId}
          folderId={props.folderId}
          formId={props.form.id}
          formName={props.form.meta.name}
        />
      ),
    });

  return (
    <div class="mt-6 flex gap-4">
      <IconSelect
        icons={SELECTABLE_FORM_ICONS}
        value={props.form.meta.icon}
        theme={props.form.meta.theme.name}
        size="md"
        onChange={(value) => {
          const formId = props.form.id;
          runWithOwner(owner, () => {
            ApiService.patchForm(formId).mutate(
              { meta: { icon: toLegacyPlatformIcon(value) } },
              {
                onError: openErrorModal,
              },
            );
          });
        }}
        disabled={!canEditForm()}
      />

      <div class="flex min-w-0 flex-col justify-between">
        <div class="text-header-xl text-primary">
          <InlineEditor
            value={props.form.meta.name}
            setValue={(value) => {
              const formId = props.form.id;
              runWithOwner(owner, () => {
                ApiService.patchForm(formId).mutate(
                  { meta: { name: value } },
                  {
                    onError: openErrorModal,
                  },
                );
              });
            }}
            label={t(($) => $.FORM_PAGE_EDIT_FORM_NAME_ARIA_LABEL)}
            open={isEditingFormName()}
            setOpen={setIsEditingFormName}
            maxLength={MAX_CHARACTERS_SM}
            schema={pickSchema(pickSchema(formSchema, "meta"), "name")}
            displayView={() => (
              <div class="flex items-center gap-1">
                <Text as="h2" text={props.form.meta.name} truncate />
                <Show when={canEditForm()}>
                  <IconButton
                    icon="pencil"
                    size="medium"
                    variant="tertiary"
                    aria-label={t(($) => $.EDIT_ARIA)}
                    onClick={() => setIsEditingFormName(true)}
                  />
                </Show>
              </div>
            )}
          />
        </div>

        <div class="text-base-medium text-secondary">
          <InlineEditor<string | undefined>
            value={props.form.meta.description ?? ""}
            setValue={(value) => {
              const formId = props.form.id;
              runWithOwner(owner, () => {
                ApiService.patchForm(formId).mutate(
                  { meta: { description: value } },
                  {
                    onError: openErrorModal,
                  },
                );
              });
            }}
            label={t(($) => $.FORM_PAGE_EDIT_FORM_DESCRIPTION_ARIA_LABEL)}
            open={isEditingFormDescription()}
            setOpen={setIsEditingFormDescription}
            maxLength={MAX_CHARACTERS_MD}
            schema={pickSchema(pickSchema(formSchema, "meta"), "description")}
            displayView={() => (
              <div class="flex items-center gap-1">
                <Switch>
                  <Match when={props.form.meta.description}>
                    {(description) => <Text as="h2" text={description()} truncate />}
                  </Match>
                  <Match when={canEditForm()}>
                    <span class="italic">{t(($) => $.FORM_PAGE_EDIT_FORM_DESCRIPTION_PLACEHOLDER)}</span>
                  </Match>
                </Switch>

                <Show when={canEditForm()}>
                  <IconButton
                    icon="pencil"
                    size="medium"
                    variant="tertiary"
                    aria-label={t(($) => $.EDIT_ARIA)}
                    onClick={() => setIsEditingFormDescription(true)}
                  />
                </Show>
              </div>
            )}
          />
        </div>
      </div>

      <div class="ml-auto flex items-center gap-2">
        <Show when={canEditForm()}>
          <EditFormButton formId={props.form.id} />
        </Show>
        <Show when={canShareForm()}>
          <DirectUrlButton formId={props.form.id} />
        </Show>

        <Show
          when={collapseActionsIntoMenu()}
          fallback={
            <>
              <Show when={canManagePermissions()}>
                <IconButton
                  icon="userAdd"
                  variant="secondary"
                  onClick={openAddPermission}
                  aria-label={t(($) => $.FORM_PAGE_PERMISSIONS_ADD_USER_ARIA)}
                />
              </Show>
              <Show when={canDeleteForm()}>
                <DeleteFormButton folderId={props.folderId} formId={props.form.id} formName={props.form.meta.name} />
              </Show>
            </>
          }
        >
          <IconButtonMenu
            aria-label={t(($) => $.ACTIONS_ARIA)}
            triggerVariant="secondary"
            triggerSize="large"
            items={[
              [
                {
                  value: "addPermission",
                  type: "action",
                  variant: "icon",
                  label: t(($) => $.FORM_PAGE_PERMISSIONS_ADD_USER_ARIA),
                  icon: { name: "userAdd", type: "outline" },
                  onClick: openAddPermission,
                },
                {
                  value: "delete",
                  type: "action",
                  variant: "icon",
                  label: t(($) => $.DELETE_ARIA),
                  icon: { name: "trash", type: "outline" },
                  onClick: openDeleteForm,
                },
              ],
            ]}
          />
        </Show>
      </div>
    </div>
  );
};

// We don't know the folder id when coming from the editor (closing/publishing), but need it in order to show the active
// folder in the sidebar. This component resolves the folder id for the given form and redirects the user to the full URL.
export const FormPageWithoutFolderId: Component<RouteSectionProps> = (props) => {
  const navigate = useNavigate();
  const customer = useCustomerState();
  const folders = ApiService.getFolders();

  createEffect(() => {
    if (isNil(folders.data)) {
      return;
    }

    const formId = FormId.from(props.params.formId!);
    const folder = folders.data.find((folder) => folder.forms.some((form) => formId.equals(form.id)));

    if (isNil(folder)) {
      navigate(`/customers/${customer.customerId}`);
      logger.warn("Unable to find folder for form", { formId });
    } else {
      navigate(`/customers/${customer.customerId}/folders/${folder.id}/forms/${formId}`);
    }
  });

  return <></>;
};

export default FormPage;
